/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Input,
  Label,
  Table,
  Sidebar,
  Spacer,
  Description,
} from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { InlineEditRole } from "~/components/Role/InlineEditRole";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { ERROR_TEXT } from "~/constants/text";
import { EmployeeRole } from "~/models";
import { ACTIONS, Can } from "~/routing";
import { generateActionSuccessText, useAxios } from "~/tools";
import { Values } from "./types";

type ManageRolesProps = {
  refetchRoles?: () => void;
  fetchTeam: () => void;
};

function ManageRoles({ refetchRoles, fetchTeam }: ManageRolesProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [roles, setRoles] = React.useState([]);
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchRoles = React.useCallback(async () => {
    try {
      const { data } = await Axios().get("/roles?limit=100");
      setRoles(data?.roles);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, showError]);

  const onSubmit = async (values: Values) => {
    try {
      await Axios().post(`/roles`, values);
      refetchRoles && refetchRoles();
      fetchRoles();
      setValue("name", "");
      showSuccess(generateActionSuccessText(`Role`)()("added")());
      fetchTeam();
    } catch (e) {
      showError(e?.response?.data?.error || ERROR_TEXT.default);
    }
  };

  React.useEffect(() => {
    isSidebarOpen && fetchRoles();
  }, [fetchRoles, isSidebarOpen]);

  const { register, handleSubmit, setValue, errors, watch } = useForm();
  const watcher = watch();

  React.useEffect(() => {
    register({ name: "name" }, { required: true });
  }, [register]);

  return (
    <>
      <Can action={ACTIONS.EDIT_ROLES}>
        <Button
          type="button"
          onClick={() => setIsSidebarOpen(true)}
          icon="settings"
          severity="medium"
        >
          Manage Roles
        </Button>
      </Can>

      <Sidebar
        title="Manage Roles"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <Table small hoverline>
          <Table.thead>
            <Table.tr>
              <Table.th css={{ width: "100%" }}>Role Name</Table.th>
              <Table.th css={{ textAlign: "center" }}>Actions</Table.th>
            </Table.tr>
          </Table.thead>
          <Table.tbody>
            {roles?.map((r: EmployeeRole) => (
              <InlineEditRole
                key={r.id}
                refetchRoles={refetchRoles ? refetchRoles : fetchRoles}
                initialValues={r}
                fetchTeam={fetchTeam}
              />
            ))}
          </Table.tbody>
        </Table>
        <Spacer height="1rem" />

        <Fieldset>
          <Label name="Create New Role" required />
          <Input
            type="text"
            name="name"
            defaultValue=""
            placeholder="Create New Role"
            ref={register({ required: true })}
          />
          {errors.name && <Description>Name is required.</Description>}
        </Fieldset>

        <FlexContainer>
          <Button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            disabled={!watcher.name}
          >
            Add Role
          </Button>
          <Button
            type="button"
            severity="medium"
            css={{ marginLeft: "auto" }}
            onClick={() => setIsSidebarOpen(false)}
          >
            Cancel
          </Button>
        </FlexContainer>
      </Sidebar>
    </>
  );
}

export { ManageRoles };
